/**
 * DEMO-ONLY: sessionStorage-backed notification feed for the header bell.
 * Removable with the mock interceptor / demo extension.
 */
import { PresidioNotificationItem } from '../models/presidio-notification.model';

/** Bumped when seed categories change so sessions pick up attention items. */
export const PRESIDIO_MOCK_NOTIFICATIONS_KEY = 'presidio-mock-notifications-v2';

const SEED: PresidioNotificationItem[] = [
  {
    id: 'n-attention-invoice',
    category: 'invoice_due',
    titleKey: 'home.attention.invoice.title',
    bodyKey: 'home.attention.invoice.body',
    refId: 'INV-WEEK',
    link: '/account/invoices',
    createdAt: Date.parse('2026-10-13T15:00:00.000Z'),
    read: false,
  },
  {
    id: 'n-attention-quote',
    category: 'quote_expiring',
    titleKey: 'home.attention.quote.title',
    bodyKey: 'home.attention.quote.body',
    refId: 'Q-2291',
    link: '/account/quotes',
    createdAt: Date.parse('2026-10-13T14:50:00.000Z'),
    read: false,
  },
  {
    id: 'n-quote-1',
    category: 'quote_received',
    titleKey: 'shell.notifications.quote_received.title',
    bodyKey: 'shell.notifications.quote_received.body',
    refId: 'Q-2291',
    link: '/account/quotes',
    createdAt: Date.parse('2026-10-13T14:20:00.000Z'),
    read: false,
  },
  {
    id: 'n-order-appr-1',
    category: 'order_approval',
    titleKey: 'shell.notifications.order_approval.title',
    bodyKey: 'shell.notifications.order_approval.body',
    refId: 'MP-104582',
    link: '/account/requisitions/approver',
    createdAt: Date.parse('2026-10-13T13:05:00.000Z'),
    read: false,
  },
  {
    id: 'n-pricing-1',
    category: 'pricing_request',
    titleKey: 'shell.notifications.pricing_request.title',
    bodyKey: 'shell.notifications.pricing_request.body',
    refId: 'PR-1042',
    link: '/account/quotes',
    createdAt: Date.parse('2026-10-12T19:40:00.000Z'),
    read: false,
  },
  {
    id: 'n-user-appr-1',
    category: 'user_approval',
    titleKey: 'shell.notifications.user_approval.title',
    bodyKey: 'shell.notifications.user_approval.body',
    refId: 'REQ-7781',
    link: '/account/organization/users',
    createdAt: Date.parse('2026-10-12T16:10:00.000Z'),
    read: true,
  },
  {
    id: 'n-order-1',
    category: 'order_placed',
    titleKey: 'shell.notifications.order_placed.title',
    bodyKey: 'shell.notifications.order_placed.body',
    refId: 'MP-104501',
    link: '/account/orders',
    createdAt: Date.parse('2026-10-11T21:00:00.000Z'),
    read: true,
  },
];

function canUseStorage(): boolean {
  return typeof sessionStorage !== 'undefined';
}

function readRaw(): PresidioNotificationItem[] | null {
  if (!canUseStorage()) {
    return null;
  }
  const raw = sessionStorage.getItem(PRESIDIO_MOCK_NOTIFICATIONS_KEY);
  if (raw === null) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as PresidioNotificationItem[];
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function writeRaw(items: PresidioNotificationItem[]): void {
  if (!canUseStorage()) {
    return;
  }
  sessionStorage.setItem(PRESIDIO_MOCK_NOTIFICATIONS_KEY, JSON.stringify(items));
}

function sorted(items: PresidioNotificationItem[]): PresidioNotificationItem[] {
  return items.slice().sort((a, b) => b.createdAt - a.createdAt);
}

/** Ensure seed exists once per browser session. Empty array = user cleared all. */
export function ensurePresidioMockNotifications(): PresidioNotificationItem[] {
  const existing = readRaw();
  if (existing !== null) {
    return existing.map(item => ({ ...item }));
  }
  const seeded = SEED.map(item => ({ ...item }));
  writeRaw(seeded);
  return seeded;
}

export function getPresidioMockNotifications(): PresidioNotificationItem[] {
  return sorted(ensurePresidioMockNotifications());
}

export function setPresidioMockNotificationRead(id: string, read: boolean): PresidioNotificationItem[] {
  const items = ensurePresidioMockNotifications().map(item =>
    item.id === id ? { ...item, read } : { ...item }
  );
  writeRaw(items);
  return sorted(items);
}

export function markPresidioMockNotificationRead(id: string): PresidioNotificationItem[] {
  return setPresidioMockNotificationRead(id, true);
}

export function markAllPresidioMockNotificationsRead(): PresidioNotificationItem[] {
  const items = ensurePresidioMockNotifications().map(item => ({ ...item, read: true }));
  writeRaw(items);
  return sorted(items);
}

/** Remove every notification from the demo feed (session). */
export function clearPresidioMockNotifications(): PresidioNotificationItem[] {
  writeRaw([]);
  return [];
}

/** DEMO test helper */
export function resetPresidioMockNotifications(): void {
  if (!canUseStorage()) {
    return;
  }
  sessionStorage.removeItem(PRESIDIO_MOCK_NOTIFICATIONS_KEY);
}
